import { Typography } from '@oresundsbron/bridge-ui';
import { RecursivePartial, useSubscribe } from '@oresundsbron/use-form';
import { invoiceParser } from '@oresundsbron/validators';
import { reduce } from 'fp-ts/lib/Array';
import { pipe } from 'fp-ts/lib/function';
import { toEntries } from 'fp-ts/lib/Record';
import { Trans, useTranslation } from 'next-i18next';
import { FC, useMemo } from 'react';
import { z } from 'zod';
import { Data as ContactInformation } from '../../../components/Forms/ContactInformation/Business';
import { PaymentMethod as PMethod } from '../../../components/Forms/PaymentMethod';
import { useCountries } from '../../../hooks/useCountries';
import { validate } from '../../../lib/zod';
import { SignUpFormPart } from '../utils';

type Invoice = z.input<typeof invoiceParser>;

export const PaymentMethod: FC<SignUpFormPart> = ({
  namespace,
  titleTranslationKey,
  ns,
}) => {
  const countries = useCountries();

  const { t } = useTranslation(ns, {
    nsMode: 'fallback',
  });

  const [address, country, city, zipCode] = useSubscribe<ContactInformation>(
    'contactInformation',
    'address',
    'country',
    'city',
    'zipCode'
  ) as string[];

  const defaultInvoice = useMemo(
    (): RecursivePartial<Invoice> => ({
      address,
      country,
      city,
      zipCode,
      sendToEmail: 'true',
      displayInEuro: '',
    }),
    [address, country, city, zipCode]
  );

  return (
    <PMethod
      namespace={namespace}
      title={t(titleTranslationKey)}
      description={t('form.sections.paymentMethod.description')}
      defaultValues={{
        invoice: defaultInvoice,
      }}
      labels={{
        address: t('form.labels.address'),
        email: t('form.labels.email'),
        city: t('form.labels.city'),
        method: {
          invoice: t('form.labels.invoice'),
          card: t('form.labels.card'),
        },
        country: t('form.labels.country'),
        attentionName: t('form.labels.attentionName'),
        zipCode: t('form.labels.zipCode'),
        useInvoiceAddress: t('form.labels.useInvoiceAddress'),
        displayInEuro: t('form.labels.displayInEuro'),
        sendToEmail: {
          true: t('form.labels.sendByEmail'),
          false: t('form.labels.sendByMail'),
        },
        loading: t('action.loading'),
        requiredFields: t('form.requiredFields'),
        chooseMethod: t('form.labels.chooseMethod'),
        invoiceDelivery: t('form.labels.invoiceDelivery'),
        paperInvoiceTitle: t('form.sections.paymentMethod.paperInvoiceTitle'),
      }}
      countryOptions={countries}
      disclaimers={{
        invoice: (
          <Trans
            i18nKey="form.sections.paymentMethod.disclaimers.invoice"
            ns={ns}
          >
            <Typography as="span" weight="medium" size="sm" />
          </Trans>
        ),
        paper: t('form.sections.paymentMethod.disclaimers.paper'),
      }}
      placeholders={{
        country: t('form.placeholders.country'),
      }}
      onValidate={async (data) => {
        if (data.method !== 'invoice') {
          return undefined;
        }

        const result = await validate(invoiceParser.spa)(data.invoice);
        if (!result) {
          return undefined;
        }

        return pipe(
          result as Record<string, string>,
          toEntries,
          reduce({}, (res, [key, value]) => ({
            ...res,
            [`invoice.${key}`]: value,
          }))
        );
      }}
    />
  );
};
